import React, { useState } from 'react';
import { Menu, X, Siren } from 'lucide-react';
import { useLang } from '../../i18n/LangContext';
import { BRAND } from '../../data/siteData';
import WhatsAppIcon from './WhatsAppIcon';

const WHATSAPP_LINK = "https://wa.me/1" + BRAND.phone.replace(/\D/g, "");

const navLinks = (t) => [
  { label: t.nav.services, href: "#services" },
  { label: t.nav.outdoor, href: "#outdoor-living" },
  { label: t.nav.permitting, href: "#permitting" },
  { label: t.nav.visualizer, href: "#visualizer" },
  { label: t.nav.technology, href: "/construction-technology-automation" },
  { label: t.nav.projects, href: "/projects" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  const handleClick = (href) => {
    setOpen(false);
    if (href.startsWith('/')) {
      window.location.href = href;
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0D0F]/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2 sm:gap-3 min-w-0 max-w-[58%] sm:max-w-none sm:flex-shrink-0" data-testid="navbar-logo">
            <img src="/dviso-logo.png" alt="Dviso Construction" className="h-10 sm:h-12 w-auto object-contain flex-shrink-0" />
            <div className="flex flex-col min-w-0 leading-none">
              {/* Mobile lockup */}
              <span className="sm:hidden text-white font-bold text-[11px] leading-tight tracking-wide font-outfit truncate">MIAMI MULTI-TRADE</span>
              <span className="sm:hidden text-[#D4AF37] text-[8px] tracking-[0.15em] uppercase leading-tight mt-0.5">Construction</span>
              {/* Desktop lockup — name only to avoid crowding the nav */}
              <span className="hidden sm:block text-white font-bold text-base tracking-wide font-outfit whitespace-nowrap">MIAMI MULTI-TRADE</span>
            </div>
          </a>

          <div className="hidden xl:flex items-center gap-3">
            {navLinks(t).map(link => (
              <button key={link.href} onClick={() => handleClick(link.href)}
                className="text-slate-300 hover:text-white text-[13px] tracking-wide transition-colors whitespace-nowrap flex-shrink-0">
                {link.label}
              </button>
            ))}

            {/* Language Selector */}
            <div className="flex items-center rounded-md overflow-hidden text-xs font-bold border border-[#D4AF37]/60 flex-shrink-0" data-testid="lang-selector">
              <button onClick={() => setLang('en')} aria-label="English"
                className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37] hover:bg-[#2A3038]'}`}>EN</button>
              <button onClick={() => setLang('es')} aria-label="Español"
                className={`px-2.5 py-1.5 transition-colors border-l border-[#D4AF37]/60 ${lang === 'es' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37] hover:bg-[#2A3038]'}`}>ES</button>
            </div>

            <a href="/emergency-services" data-testid="nav-emergency-cta"
              className="flex items-center gap-1.5 border border-[#E11D2A]/50 bg-[#E11D2A]/10 hover:bg-[#E11D2A] text-[#FF6B73] hover:text-white font-semibold text-[13px] px-3 py-2.5 rounded transition-all whitespace-nowrap flex-shrink-0">
              <Siren className="w-4 h-4" />{t.emergency.navBtn}
            </a>
            <button onClick={() => handleClick('#estimate')} data-testid="nav-estimate-cta"
              className="bg-[#D4AF37] hover:bg-[#E5C158] text-black font-semibold text-[13px] px-4 py-2.5 rounded transition-colors whitespace-nowrap flex-shrink-0">
              {t.nav.estimate}
            </button>
          </div>

          <div className="flex xl:hidden items-center gap-2">
            <div className="flex items-center rounded-md overflow-hidden text-xs font-bold border border-[#D4AF37]/60" data-testid="lang-selector-mobile">
              <button onClick={() => setLang('en')} aria-label="English"
                className={`px-2.5 py-1 transition-colors ${lang === 'en' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37]'}`}>EN</button>
              <button onClick={() => setLang('es')} aria-label="Español"
                className={`px-2.5 py-1 transition-colors border-l border-[#D4AF37]/60 ${lang === 'es' ? 'bg-[#D4AF37] text-black' : 'bg-[#1E232B] text-[#D4AF37]'}`}>ES</button>
            </div>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="mobile-topbar-whatsapp" aria-label="WhatsApp" className="bg-[#25D366] text-white p-2 rounded-full">
              <WhatsAppIcon className="w-4 h-4" />
            </a>
            <button onClick={() => setOpen(!open)} className="text-white p-2" data-testid="mobile-menu-btn">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-[#0B0D0F] border-t border-white/10">
          <div className="px-4 py-4 space-y-1">
            {navLinks(t).map(link => (
              <button key={link.href} onClick={() => handleClick(link.href)}
                className="block w-full text-left text-slate-300 hover:text-white py-3 px-3 rounded hover:bg-white/5 transition-colors">
                {link.label}
              </button>
            ))}
            <a href="/emergency-services" data-testid="mobile-nav-emergency"
              className="flex items-center justify-center gap-2 w-full border border-[#E11D2A]/50 bg-[#E11D2A]/10 text-[#FF6B73] font-semibold py-3 rounded mt-3 transition-all">
              <Siren className="w-4 h-4" />{t.emergency.navBtn}
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp"
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-black font-semibold py-3 rounded mt-2 transition-all">
              <WhatsAppIcon className="w-4 h-4" />{t.footer.whatsapp}
            </a>
            <button onClick={() => handleClick('#estimate')}
              className="w-full bg-[#D4AF37] hover:bg-[#E5C158] text-black font-semibold py-3 rounded mt-2 transition-colors">
              {t.nav.estimate}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
